/**
 * Home Page - Landing Page
 * Mobile First Design
 */

import React from 'react';
import { 
  Heart, Sparkles, Shield, Users, Clock, RefreshCw, 
  ChevronLeft, Star, CheckCircle, ArrowLeft
} from 'lucide-react';

const FEATURES = [
  {
    icon: '🤖',
    title: 'خالتك الذكية',
    description: 'خالتي هيلة تستخدم الذكاء الاصطناعي عشان تفهم شخصيتك وتلاقي لك الشخص المناسب'
  },
  {
    icon: '🔒',
    title: 'سرّك في بير',
    description: 'بياناتك مشفرة ومحمية، ما يشوفها أحد إلا اللي توافق عليه'
  },
  {
    icon: '💕',
    title: 'راس براس',
    description: 'توافق واحد في كل مرة، لأن الجد يبي تركيز'
  },
  {
    icon: '⏰',
    title: '7 أيام تعارف',
    description: 'وقت كافي تتعرفون على بعض، وإذا احتجتوا أكثر؟ خالتي هيلة تمدد لكم'
  },
  {
    icon: '🔄',
    title: 'مو متأكد؟ لا تشيل هم',
    description: 'تقدر تجدد فترة التعارف إذا تحتاج وقت أكثر، أو تنسحب بأي لحظة بدون إحراج'
  },
  {
    icon: '🇸🇦',
    title: 'سعودية 100%',
    description: 'مخصصة للسعوديين، تفهم ثقافتنا وتحترم عاداتنا'
  }
];

const STEPS = [
  { icon: '📝', title: 'سجّل عند خالتي', description: 'أنشئ حسابك وجاوب على أسئلة خالتي هيلة عشان تتعرف عليك' },
  { icon: '🤖', title: 'خالتي تدور لك', description: 'الذكاء الاصطناعي يحلل إجاباتك ويلاقي لك الشخص المناسب' },
  { icon: '💕', title: 'تعرّف على توافقك', description: 'خالتي هيلة ترشح لك شخص واحد في كل مرة عشان تركز' },
  { icon: '💬', title: 'سولفوا 7 أيام', description: 'تعارفوا وشوفوا إذا فيه نصيب، ومحتاج وقت أكثر؟ مددوا' },
  { icon: '📱', title: 'تبادلوا التواصل', description: 'إذا حسيتوا إن فيه توافق، شاركوا معلومات التواصل وأكملوا بالحلال' },
];

const TESTIMONIALS = [
  { name: 'أبو فهد', text: 'ما توقعت إن خالتي هيلة تلاقي لي بهالسرعة! الحمدلله تزوجنا 💕', rating: 5 },
  { name: 'أم سارة', text: 'أخيراً منصة تحترم خصوصيتنا وتفهم ثقافتنا، شكراً خالتي هيلة', rating: 5 },
  { name: 'خالد', text: 'أحسن شي إنك تركز على شخص واحد، بدون تشتت', rating: 5 },
];

export default function Home({ onStartClick }) {
  return (
    <div className="overflow-hidden">
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 pt-16">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-64 h-64 bg-rose-200/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-100/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-32">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-6 md:mb-8">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">مدعوم بالذكاء الاصطناعي</span>
            </div>

            {/* Main Heading */}
            <div className="text-6xl md:text-8xl mb-4">👩‍🦳💕</div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              <span className="bg-gradient-to-r from-rose-600 via-red-600 to-purple-600 bg-clip-text text-transparent">
                خالتي هيلة
              </span>
              <br />
              <span className="text-gray-800 text-2xl md:text-4xl lg:text-5xl">
                تربط راسين بالحلال
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              من زمان وخالتي هيلة تعرف كيف توفّق بين الناس...
              <br className="hidden md:block" />
              اليوم صارت أذكى بالذكاء الاصطناعي 🤖
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={onStartClick}
                className="w-full sm:w-auto px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-rose-500 via-red-500 to-purple-600 rounded-full hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 group"
              >
                يلا نبدأ مع خالتي هيلة
                <Heart className="w-5 h-5 fill-white group-hover:scale-125 transition-transform" />
              </button>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-gray-700 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-full hover:border-rose-300 hover:bg-rose-50 transition-all flex items-center justify-center gap-2"
              >
                كيف تشتغل؟
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span>بيانات مشفرة</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span>للسعوديين فقط</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600" />
                <span>مجاني 100%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-gray-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              ليش خالتي هيلة؟
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              لأنها تجمع بين حنان الخالة وذكاء التقنية
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {FEATURES.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="text-4xl md:text-5xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 to-rose-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              كيف تشتغل خالتي هيلة؟
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              خمس خطوات بسيطة وتلاقي نصيبك
            </p>
          </div>

          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden md:block absolute top-24 left-1/2 -translate-x-1/2 w-[80%] h-1 bg-gradient-to-r from-rose-200 via-purple-200 to-rose-200 rounded-full" />
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
              {STEPS.map((step, index) => (
                <div key={index} className="relative text-center group">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-r from-rose-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg md:hidden">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center text-4xl md:text-5xl mx-auto mb-4 group-hover:scale-110 group-hover:shadow-xl transition-all">
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  
                  {/* Arrow - Mobile */}
                  {index < STEPS.length - 1 && (
                    <div className="md:hidden flex justify-center my-4">
                      <ArrowLeft className="w-6 h-6 text-rose-300 rotate-[-90deg]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              وش قالوا عن خالتي هيلة؟
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl p-6 md:p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                <p className="font-bold text-gray-900">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-rose-500 via-red-500 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-5xl md:text-6xl mb-6">👩‍🦳💕</div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            خالتي هيلة تنتظرك!
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl mx-auto">
            لا تطوّل على خالتك، سجّل الحين وخلها تلاقي لك نصيبك
          </p>
          <button
            onClick={onStartClick}
            className="px-10 py-4 text-lg font-bold text-rose-600 bg-white rounded-full hover:shadow-xl hover:scale-105 transition-all"
          >
            سجّل الحين مجاناً ❤️
          </button>
        </div>
      </section>

    </div>
  );
}
